/**
 * The Type and Notes text of a back-up commit row on Logs (BLU-1428,
 * BLU-1527). A back-up commit row carries no notes from the server (the
 * machine form never leaves it); the text is built from `backupCommit`.
 * Shared by the table and the CSV export, so the two never drift. Pure: no
 * React.
 *
 * Two kinds of row share the movement type, and the server says which
 * (`backupCommit.kind`):
 * - `backup`: a 3PL's back-up stock: the client's own line, and the 3PL's
 *   back-up line that took the order for that client;
 * - `own_substitute`: an org's own substitute garment: both lines the org's
 *   own, at a floor it owns.
 */
import type { TFunction } from 'i18next';

export type BackupCommitKind = 'own_substitute' | 'backup';

export interface BackupCommitInfo {
  line: 'own' | 'backup';
  kind: BackupCommitKind;
  backupSkuCode: string | null;
  clientPrice: number | null;
  clientPriceCurrency: string | null;
}

export interface MovementNotesRow {
  notes: string | null;
  movementType: string;
  quantity: number;
  actorOrganizationId: string | null;
  actorOrganizationName: string | null;
  backupCommit?: BackupCommitInfo | null;
}

export interface MovementNotesContext {
  /** The Client column's name for an org. */
  orgLabel: (id: string, name: string | null) => string;
  /** A client price in the 3PL's currency. */
  formatPrice: (amount: number, currency: string | null) => string;
}

/**
 * The Type of a back-up commit row: "Substitute garment" or "3PL back-up".
 * Never the bare "Substitution" (that is a 3PL's shared stock used by a
 * client's order, its own type).
 */
export function backupCommitTypeLabel(t: TFunction<'inventory'>, kind: BackupCommitKind | null | undefined): string {
  return kind === 'own_substitute'
    ? t('logs.movements.type.substituteGarment')
    : t('logs.movements.type.backup');
}

/** The Type filter's one option for both kinds (the filter is the server's, by movement type). */
export function backupCommitFilterLabel(t: TFunction<'inventory'>): string {
  return t('logs.movements.type.backupOrSubstitute');
}

export function movementNotesText(
  t: TFunction<'inventory'>,
  row: MovementNotesRow,
  ctx: MovementNotesContext,
): string | null {
  const bc = row.backupCommit;
  if (row.movementType !== 'backup_commit' || !bc) return row.notes;
  const ownSubstitute = bc.kind === 'own_substitute';

  if (bc.line === 'backup') {
    if (ownSubstitute) {
      return row.quantity >= 0
        ? t('logs.movements.backupCommit.substituteLineTaken')
        : t('logs.movements.backupCommit.substituteLineReleased');
    }
    const client = row.actorOrganizationId
      ? ctx.orgLabel(row.actorOrganizationId, row.actorOrganizationName)
      : t('logs.movements.backupCommit.clientFallback');
    return row.quantity >= 0
      ? t('logs.movements.backupCommit.backupLineTaken', { client })
      : t('logs.movements.backupCommit.backupLineReleased', { client });
  }

  // The main line: a negative change moved the commit onto the other SKU (the
  // 3PL's back-up, or the org's own substitute); a positive one moved it back.
  const code = bc.backupSkuCode;
  if (ownSubstitute) {
    // An own substitute has no client price: the stock is the org's own.
    return row.quantity <= 0
      ? code
        ? t('logs.movements.backupCommit.usingSubstitute', { code })
        : t('logs.movements.backupCommit.usingSubstituteNoCode')
      : code
        ? t('logs.movements.backupCommit.backFromSubstitute', { code })
        : t('logs.movements.backupCommit.backFromSubstituteNoCode');
  }
  const base =
    row.quantity <= 0
      ? code
        ? t('logs.movements.backupCommit.usingBackupSku', { code })
        : t('logs.movements.backupCommit.usingBackupSkuNoCode')
      : code
        ? t('logs.movements.backupCommit.backToOwn', { code })
        : t('logs.movements.backupCommit.backToOwnNoCode');
  return bc.clientPrice != null
    ? t('logs.movements.backupCommit.withPrice', {
        text: base,
        // The 3PL's own currency beside the amount (the Products page form),
        // never the viewer's currency.
        price: ctx.formatPrice(bc.clientPrice, bc.clientPriceCurrency),
      })
    : base;
}
